import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import {
  yieldToMainThread,
  getViewParameterValue,
  ResizeNotifier,
} from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets
 * @property {HTMLElement} [overflowList]
 */

export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() =>
    this.updateVariantPickerCss()
  );

  connectedCallback() {
    super.connectedCallback();

    const fieldsets =
      /** @type {HTMLFieldSetElement[]} */ (
        this.refs.fieldsets || []
      );

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(
        fieldset?.querySelectorAll('input') ?? []
      );

      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex(
        (radio) =>
          radio.dataset.currentChecked === 'true'
      );

      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([
          initialCheckedIndex,
        ]);
      }
    });

    this.addEventListener(
      'change',
      this.variantChanged.bind(this)
    );

    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) {
      return;
    }

    const selectedOption =
      event.target instanceof HTMLSelectElement
        ? event.target.options[
            event.target.selectedIndex
          ]
        : event.target;

    if (!selectedOption) {
      return;
    }

    this.updateSelectedOption(event.target);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl =
      this.dataset.productUrl?.split('?')[0];

    const newUrl =
      selectedOption.dataset.connectedProductUrl;

    const loadsNewProduct =
      isOnProductPage &&
      !!newUrl &&
      newUrl !== currentUrl;

    const isOnFeaturedProductSection = Boolean(
      this.closest(
        'featured-product-information'
      )
    );

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
        ? 'featured-product-information'
        : undefined;

    const {
      optionValueId = '',
      variantId = '',
      connectedProductUrl = '',
    } = selectedOption.dataset;

    this.fetchUpdatedSection(
      this.buildRequestUrl(selectedOption),
      {
        morphElementSelector,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
        },
      }
    );

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set(
          'variant',
          variantId
        );
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState(
          {},
          '',
          url.toString()
        );
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets =
      /** @type {HTMLFieldSetElement[]} */ (
        this.refs.fieldsets || []
      );

    const fieldset =
      fieldsets[fieldsetIndex];

    const checkedIndices =
      this.#checkedIndices[fieldsetIndex];

    const radios =
      this.#radios[fieldsetIndex];

    if (
      !radios ||
      !checkedIndices ||
      !fieldset
    ) {
      return null;
    }

    const [
      currentIndex,
      previousIndex,
    ] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth:
        currentIndex !== undefined
          ? radios[currentIndex]
              ?.parentElement?.offsetWidth
          : undefined,
      previousWidth:
        previousIndex !== undefined
          ? radios[previousIndex]
              ?.parentElement?.offsetWidth
          : undefined,
    };
  }

  /**
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({
    fieldset,
    currentWidth,
    previousWidth,
    currentIndex,
    previousIndex,
  }) {
    if (currentWidth) {
      fieldset.style.setProperty(
        '--pill-width-current',
        `${currentWidth}px`
      );
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty(
        '--pill-width-current'
      );
    }

    if (previousWidth) {
      fieldset.style.setProperty(
        '--pill-width-previous',
        `${previousWidth}px`
      );
    } else if (
      previousIndex !== undefined
    ) {
      fieldset.style.removeProperty(
        '--pill-width-previous'
      );
    }
  }

  /**
   * @param {number} fieldsetIndex
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) {
      return;
    }

    const measurements =
      this.#getFieldsetMeasurements(
        fieldsetIndex
      );

    if (measurements) {
      this.#applyFieldsetMeasurements(
        measurements
      );
    }
  }

  /**
   * @param {string | Element} target
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement =
        this.querySelector(
          `[data-option-value-id="${target}"]`
        );

      if (!targetElement) {
        throw new Error(
          'Target element not found'
        );
      }

      target = targetElement;
    }

    if (
      target instanceof HTMLInputElement
    ) {
      const fieldsetIndex =
        Number.parseInt(
          target.dataset.fieldsetIndex ||
            ''
        );

      const inputIndex =
        Number.parseInt(
          target.dataset.inputIndex || ''
        );

      if (
        !Number.isNaN(fieldsetIndex) &&
        !Number.isNaN(inputIndex)
      ) {
        const fieldsets =
          /** @type {HTMLFieldSetElement[]} */ (
            this.refs.fieldsets || []
          );

        const fieldset =
          fieldsets[fieldsetIndex];

        const checkedIndices =
          this.#checkedIndices[
            fieldsetIndex
          ];

        const radios =
          this.#radios[fieldsetIndex];

        if (
          radios &&
          checkedIndices &&
          fieldset
        ) {
          const [
            currentIndex,
            previousIndex,
          ] = checkedIndices;

          if (
            currentIndex !== undefined &&
            radios[currentIndex]
          ) {
            radios[
              currentIndex
            ].dataset.previousChecked =
              'false';
          }

          if (
            previousIndex !== undefined &&
            radios[previousIndex]
          ) {
            radios[
              previousIndex
            ].dataset.previousChecked =
              'false';
          }

          checkedIndices.unshift(
            inputIndex
          );

          checkedIndices.length =
            Math.min(
              checkedIndices.length,
              2
            );

          const newCurrentIndex =
            checkedIndices[0];

          const newPreviousIndex =
            checkedIndices[1];

          if (
            newCurrentIndex !==
              undefined &&
            radios[newCurrentIndex]
          ) {
            radios[
              newCurrentIndex
            ].dataset.currentChecked =
              'true';
          }

          if (
            newPreviousIndex !==
              undefined &&
            radios[newPreviousIndex]
          ) {
            radios[
              newPreviousIndex
            ].dataset.previousChecked =
              'true';

            radios[
              newPreviousIndex
            ].dataset.currentChecked =
              'false';
          }

          this.updateFieldsetCss(
            fieldsetIndex
          );
        }
      }

      target.checked = true;
    }

    if (
      target instanceof HTMLSelectElement
    ) {
      const newValue = target.value;

      const newSelectedOption =
        Array.from(target.options).find(
          (option) =>
            option.value === newValue
        );

      if (!newSelectedOption) {
        throw new Error(
          'Option not found'
        );
      }

      for (
        const option of target.options
      ) {
        option.removeAttribute(
          'selected'
        );
      }

      newSelectedOption.setAttribute(
        'selected',
        'selected'
      );
    }
  }

  /**
   * @param {HTMLElement} selectedOption
   * @param {string | null} [source]
   * @param {string[]} [sourceSelectedOptionsValues]
   * @returns {string}
   */
  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = []
  ) {
    let productUrl =
      selectedOption.dataset
        .connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;

    this.#pendingRequestUrl =
      productUrl;

    const params = [];
    const viewParamValue =
      getViewParameterValue();

    if (viewParamValue) {
      params.push(
        `view=${viewParamValue}`
      );
    }

    if (
      this.selectedOptionsValues
        .length &&
      !source
    ) {
      params.push(
        `option_values=${this.selectedOptionsValues.join(',')}`
      );
    } else if (
      source === 'product-card'
    ) {
      if (
        this.selectedOptionsValues
          .length
      ) {
        params.push(
          `option_values=${sourceSelectedOptionsValues.join(',')}`
        );
      } else {
        params.push(
          `option_values=${selectedOption.dataset.optionValueId}`
        );
      }
    }

    const SECTION_ID_MAP = {
      'quick-add-component':
        'section-rendering-product-card',
      'swatches-variant-picker-component':
        'section-rendering-product-card',
      'featured-product-information':
        this.closest(
          'featured-product-information'
        )?.id,
    };

    const closestSectionId =
      /** @type {keyof typeof SECTION_ID_MAP | undefined} */ (
        Object.keys(
          SECTION_ID_MAP
        ).find((sectionId) =>
          this.closest(sectionId)
        )
      );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl =
          productUrl.split('?')[0];
      }

      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * @param {string} requestUrl
   * @param {object} [options]
   * @param {string} [options.morphElementSelector]
   * @param {{
   * optionValueId?: string,
   * variantId?: string,
   * connectedProductUrl?: string
   * }} [options.detail]
   */
  fetchUpdatedSection(
    requestUrl,
    {
      morphElementSelector,
      detail = {},
    } = {}
  ) {
    const {
      optionValueId = '',
      variantId,
      connectedProductUrl = '',
    } = detail;

    this.#abortController?.abort();

    this.#abortController =
      new AbortController();

    const deferredEventPromise =
      ProductSelectEvent.createPromise();

    const selectedOptions =
      this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id:
            this.dataset.productId ??
            '',
          title:
            this.dataset.productTitle ??
            '',
          handle:
            this.dataset.productHandle ??
            '',
        },
        selectedOptions,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
        },
        promise:
          deferredEventPromise.promise,
      })
    );

    fetch(requestUrl, {
      signal:
        this.#abortController.signal,
    })
      .then((response) =>
        response.text()
      )
      .then(async (responseText) => {
        let html =
          new DOMParser().parseFromString(
            responseText,
            'text/html'
          );

        // Resolve an unavailable size before notifying the gallery.
        // The old delayed click briefly selected a different color's media.
        if (!morphElementSelector) {
          const picker = html.querySelector('variant-picker');
          const selectedValues = [];
          let needsAnotherRequest = false;

          for (const fieldset of picker?.querySelectorAll('fieldset') ?? []) {
            let selected = fieldset.querySelector('input[type="radio"]:checked') ||
              fieldset.querySelector('input[type="radio"][data-current-checked="true"]');

            if (selected?.disabled || selected?.dataset.optionAvailable === 'false' ||
                selected?.getAttribute('aria-disabled') === 'true') {
              const available = Array.from(fieldset.querySelectorAll('input[type="radio"]'))
                .find((option) => !option.disabled &&
                  option.dataset.optionAvailable !== 'false' &&
                  option.getAttribute('aria-disabled') !== 'true');
              if (available) {
                selected = available;
                needsAnotherRequest = true;
              }
            }

            if (selected?.dataset.optionValueId) {
              selectedValues.push(selected.dataset.optionValueId);
            }
          }

          for (const select of picker?.querySelectorAll('select') ?? []) {
            const selected = select.selectedOptions[0];
            if (selected?.dataset.optionValueId) {
              selectedValues.push(selected.dataset.optionValueId);
            }
          }

          if (needsAnotherRequest && selectedValues.length) {
            const correctedUrl = new URL(requestUrl, window.location.origin);
            correctedUrl.searchParams.set('option_values', selectedValues.join(','));
            const correctedResponse = await fetch(correctedUrl, {
              signal: this.#abortController.signal,
            });
            html = new DOMParser().parseFromString(
              await correctedResponse.text(), 'text/html'
            );
          }
        }

        this.#pendingRequestUrl = undefined;

        html
          .querySelector(
            'overflow-list[defer]'
          )
          ?.removeAttribute('defer');

        const variantPickerJsonScript =
          html.querySelector(
            'variant-picker script[type="application/json"]'
          );

        const textContent =
          variantPickerJsonScript
            ?.textContent;

        if (!textContent) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId:
                this.dataset
                  .productId ?? '',
              sourceId:
                this.selectedOptionId,
              resource: null,
            },
          });

          return;
        }

        let newProduct;

        if (
          morphElementSelector ===
          'main'
        ) {
          this.updateMain(html);
        } else if (
          morphElementSelector
        ) {
          this.updateElement(
            html,
            morphElementSelector
          );
        } else {
          const { overflowList } =
            this.refs;

          const wasSwatchesExpanded =
            overflowList instanceof
              OverflowList &&
            overflowList.getAttribute(
              'disabled'
            ) === 'true';

          newProduct =
            this.updateVariantPicker(
              html
            );

          if (
            wasSwatchesExpanded
          ) {
            const overflowListAfterMorph =
              overflowList;

            if (
              overflowListAfterMorph instanceof
              OverflowList
            ) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        if (this.selectedOptionId) {
          const variantData =
            JSON.parse(textContent);

          if (
            variantData &&
            typeof variantData ===
              'object'
          ) {
            const productViewAttr =
              variantPickerJsonScript
                ?.closest(
                  '[view-event-payload]'
                )
                ?.getAttribute(
                  'view-event-payload'
                )
                ?.trim();

            deferredEventPromise.resolve({
              variant:
                (productViewAttr &&
                  JSON.parse(
                    productViewAttr
                  ))?.product
                  ?.selectedVariant ??
                null,
              detail: {
                html,
                productId:
                  this.dataset
                    .productId ?? '',
                newProduct,
                sourceId:
                  this.selectedOptionId,
                resource: variantData,
              },
            });

            return;
          }
        }

        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId:
              this.dataset.productId ??
              '',
            newProduct,
            sourceId:
              this.selectedOptionId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        deferredEventPromise.reject(
          error
        );

        if (
          error.name === 'AbortError'
        ) {
          console.warn(
            'Fetch aborted by user'
          );
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * @param {Document | Element} newHtml
   * @returns {NewProduct | undefined}
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource =
      newHtml.querySelector(
        this.tagName.toLowerCase()
      );

    if (!newVariantPickerSource) {
      throw new Error(
        'No new variant picker source found'
      );
    }

    if (
      newVariantPickerSource instanceof
      HTMLElement
    ) {
      const newProductId =
        newVariantPickerSource.dataset
          .productId;

      const newProductUrl =
        newVariantPickerSource.dataset
          .productUrl;

      if (
        newProductId &&
        newProductUrl &&
        this.dataset.productId !==
          newProductId
      ) {
        newProduct = {
          id: newProductId,
          url: newProductUrl,
        };
      }

      this.dataset.productId =
        newProductId;

      this.dataset.productUrl =
        newProductUrl;
    }

    morph(
      this,
      newVariantPickerSource,
      {
        ...MORPH_OPTIONS,
        getNodeKey: (node) => {
          if (
            !(
              node instanceof
              HTMLElement
            )
          ) {
            return undefined;
          }

          return node.dataset.key;
        },
      }
    );

    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets =
      /** @type {HTMLFieldSetElement[]} */ (
        this.refs.fieldsets || []
      );

    const measurements =
      fieldsets
        .map((_, index) =>
          this.#getFieldsetMeasurements(
            index
          )
        )
        .filter(
          (measurement) =>
            measurement !== null
        );

    for (
      const measurement of measurements
    ) {
      this.#applyFieldsetMeasurements(
        measurement
      );
    }
  }

  /**
   * @param {Document} newHtml
   * @param {string} elementSelector
   */
  updateElement(
    newHtml,
    elementSelector
  ) {
    const element =
      this.closest(elementSelector);

    const newElement =
      newHtml.querySelector(
        elementSelector
      );

    if (!element || !newElement) {
      throw new Error(
        `No new element source found for ${elementSelector}`
      );
    }

    morph(element, newElement);
  }

  /**
   * @param {Document} newHtml
   */
  updateMain(newHtml) {
    const main =
      document.querySelector('main');

    const newMain =
      newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error(
        'No new main source found'
      );
    }

    morph(main, newMain);
  }

  /**
   * @returns {
   * HTMLInputElement |
   * HTMLOptionElement |
   * undefined
   * }
   */
  get selectedOption() {
    const selectedOption =
      this.querySelector(
        'select option[selected], fieldset input:checked'
      );

    if (
      !(
        selectedOption instanceof
          HTMLInputElement ||
        selectedOption instanceof
          HTMLOptionElement
      )
    ) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * @returns {{
   * name: string,
   * value: string
   * }[]}
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    for (
      const select of this.querySelectorAll(
        'select'
      )
    ) {
      const selected =
        select.selectedOptions[0];

      if (
        selected?.dataset?.optionName
      ) {
        options.push({
          name:
            selected.dataset
              .optionName,
          value: selected.value,
        });
      }
    }

    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs =
      this.querySelectorAll(
        'fieldset input:checked'
      );

    for (
      const input of checkedInputs
    ) {
      if (
        input.dataset?.optionName
      ) {
        options.push({
          name:
            input.dataset
              .optionName,
          value: input.value,
        });
      }
    }

    return options;
  }

  /**
   * @returns {string | undefined}
   */
  get selectedOptionId() {
    const { selectedOption } =
      this;

    if (!selectedOption) {
      return undefined;
    }

    const { optionValueId } =
      selectedOption.dataset;

    if (!optionValueId) {
      throw new Error(
        'No option value ID found'
      );
    }

    return optionValueId;
  }

  /**
   * @returns {string[]}
   */
  get selectedOptionsValues() {
    /** @type {HTMLElement[]} */
    const selectedOptions =
      Array.from(
        this.querySelectorAll(
          'select option[selected], fieldset input:checked'
        )
      );

    return selectedOptions.map(
      (option) => {
        const { optionValueId } =
          option.dataset;

        if (!optionValueId) {
          throw new Error(
            'No option value ID found'
          );
        }

        return optionValueId;
      }
    );
  }
}

if (
  !customElements.get(
    'variant-picker'
  )
) {
  customElements.define(
    'variant-picker',
    VariantPicker
  );
}